import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import {
  DAY_OF_WEEK_LABELS,
  EMAIL_MODE_LABELS,
  hourLabel,
  isSystemCategory,
  notificationTypeDisplay,
  type NotificationPreference,
  type NotificationPreferencesResponse,
  type NotificationUserSettings,
  type ServedNotificationType,
  type TenantNotificationContext,
  type UserEmailMode
} from "../lib/notifications.js";

/**
 * Notification preferences page (ADR-0044, extended by ADR-0171 §4.2).
 * Route: `/app/settings/notifications`.
 *
 * Per-type rows keep the category-grouped table and per-control autosave. The
 * email column is a MODE select served per type (default / immediately /
 * bundled / digest / no email) — 'Default' follows the tenant's routing.
 * Above the table: the user's digest schedule (inheriting the tenant default)
 * and quiet hours (hold-and-bundle — email waits, nothing is lost).
 *
 * Opt-out model — a missing preference row = default mode + in-system on.
 */

const CATEGORY_ORDER: string[] = ["transactional", "digest", "subscription", "system"];

function orderedCategories(present: readonly string[]): string[] {
  const known = CATEGORY_ORDER.filter((c) => present.includes(c));
  const rest = present.filter((c) => !CATEGORY_ORDER.includes(c)).sort();
  return [...known, ...rest];
}
const CATEGORY_LABEL: Record<string, string> = {
  transactional: "Transactional",
  digest: "Digest",
  subscription: "Subscriptions",
  system: "System"
};

type PrefState = Record<string, { email_mode: UserEmailMode; in_system_enabled: boolean }>;

function defaultState(served: readonly ServedNotificationType[]): PrefState {
  const out: PrefState = {};
  for (const s of served) out[s.type] = { email_mode: "default", in_system_enabled: true };
  return out;
}

function mergePreferences(rows: NotificationPreference[], served: readonly ServedNotificationType[]): PrefState {
  const out = defaultState(served);
  for (const r of rows) {
    out[r.notification_type] = {
      email_mode: r.email_mode ?? "default",
      in_system_enabled: r.in_system_enabled
    };
  }
  return out;
}

const HOURS = Array.from({ length: 24 }, (_, h) => h);

const ROLE_LABEL: Record<string, string> = {
  responsible: "Responsible",
  accountable: "Accountable",
  support: "Support",
  consulted: "Consulted",
  informed: "Informed"
};

const FLOOR_PHRASE: Record<string, string> = {
  digest: "digest",
  bundled: "bundled",
  immediate: "immediate"
};

export function SettingsNotifications() {
  const [state, setState] = useState<PrefState>({});
  const [servedTypes, setServedTypes] = useState<ServedNotificationType[]>([]);
  const [settings, setSettings] = useState<NotificationUserSettings | null>(null);
  const [tenant, setTenant] = useState<TenantNotificationContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingType, setSavingType] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<NotificationPreferencesResponse>("/api/tenant/notifications/preferences");
      const served = res.types ?? [];
      setServedTypes(served);
      setState(mergePreferences(res.preferences, served));
      setSettings(res.settings ?? null);
      setTenant(res.tenant ?? null);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function savePreference(
    type: string,
    patch: Partial<{ email_mode: UserEmailMode; in_system_enabled: boolean }>
  ) {
    const prev = state;
    setState({ ...state, [type]: { ...state[type], ...patch } });
    setSavingType(type);
    try {
      await apiFetch("/api/tenant/notifications/preferences", {
        method: "PATCH",
        body: { preferences: [{ notification_type: type, ...patch }] }
      });
    } catch (e) {
      setState(prev);
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setSavingType(null);
    }
  }

  async function saveSettings(patch: Record<string, unknown>) {
    const prev = settings;
    setSavingSettings(true);
    try {
      const res = await apiFetch<{ settings: NotificationUserSettings }>("/api/tenant/notifications/settings", {
        method: "PATCH",
        body: patch
      });
      setSettings(res.settings);
    } catch (e) {
      setSettings(prev);
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setSavingSettings(false);
    }
  }

  const grouped = useMemo(() => {
    const byCategory: Record<string, ServedNotificationType[]> = {};
    for (const s of servedTypes) (byCategory[s.category] ??= []).push(s);
    return byCategory;
  }, [servedTypes]);

  const flooredRoles = useMemo(() => {
    if (!tenant) return [] as Array<{ role: string; mode: string }>;
    return Object.entries(tenant.floors_effective)
      .filter(([, mode]) => mode !== "none")
      .map(([role, mode]) => ({ role, mode }));
  }, [tenant]);

  // The digest-schedule card's control state. "Tenant default" = no override
  // stored (NULL cadence); choosing it clears the whole override.
  const digestCadence = settings?.digest_cadence ?? null;
  const effectiveDigest = tenant?.digest_effective ?? { cadence: "weekly" as const, day_of_week: 1, hour: 8 };
  const tenantDefaultLabel =
    effectiveDigest.cadence === "weekly"
      ? `Tenant default (Weekly, ${DAY_OF_WEEK_LABELS[effectiveDigest.day_of_week]}, ${hourLabel(effectiveDigest.hour)})`
      : `Tenant default (Daily, ${hourLabel(effectiveDigest.hour)})`;

  const quietOn = settings?.quiet_hours_start != null && settings?.quiet_hours_end != null;

  return (
    <PageLayout title="Notifications">
      {error && <ErrorState detail={error} />}
      {loading && <LoadingState />}
      {!loading && !error && (
        <div style={{ display: "grid", gap: 12 }}>
          <div className="card">
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Digest schedule</div>
            <div className="inputRow" style={{ gap: 8 }}>
              <label className="field" style={{ minWidth: 260 }}>
                <span className="label">Schedule</span>
                <select
                  className="select"
                  value={digestCadence ?? ""}
                  disabled={savingSettings}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "") {
                      // Back to the tenant default: clear the whole override.
                      void saveSettings({ digest_cadence: null, digest_day_of_week: null, digest_hour: null });
                    } else {
                      void saveSettings({
                        digest_cadence: v,
                        digest_day_of_week: settings?.digest_day_of_week ?? effectiveDigest.day_of_week,
                        digest_hour: settings?.digest_hour ?? effectiveDigest.hour
                      });
                    }
                  }}
                >
                  <option value="">{tenantDefaultLabel}</option>
                  <option value="weekly">Weekly</option>
                  <option value="daily">Daily</option>
                </select>
              </label>
              <label className="field">
                <span className="label">Day</span>
                <select
                  className="select"
                  value={settings?.digest_day_of_week ?? effectiveDigest.day_of_week}
                  disabled={savingSettings || digestCadence !== "weekly"}
                  onChange={(e) => void saveSettings({ digest_day_of_week: Number(e.target.value) })}
                >
                  {DAY_OF_WEEK_LABELS.map((d, i) => (
                    <option key={d} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="label">Hour</span>
                <select
                  className="select"
                  value={settings?.digest_hour ?? effectiveDigest.hour}
                  disabled={savingSettings || digestCadence == null}
                  onChange={(e) => void saveSettings({ digest_hour: Number(e.target.value) })}
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {hourLabel(h)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="help">
              When your digest email arrives. Hours are in your organization's timezone
              {tenant ? ` (${tenant.timezone})` : ""}.
            </div>
          </div>

          <div className="card">
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Quiet hours</div>
            <div className="inputRow" style={{ gap: 8, alignItems: "center" }}>
              <label className="field" style={{ flexDirection: "row", display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={quietOn}
                  disabled={savingSettings}
                  onChange={(e) => {
                    if (e.target.checked) {
                      void saveSettings({ quiet_hours_start: 22, quiet_hours_end: 6 });
                    } else {
                      void saveSettings({ quiet_hours_start: null, quiet_hours_end: null });
                    }
                  }}
                  aria-label="Enable quiet hours"
                />
                <span className="label" style={{ margin: 0 }}>
                  Hold email during quiet hours
                </span>
              </label>
              {quietOn && (
                <>
                  <label className="field">
                    <span className="label">From</span>
                    <select
                      className="select"
                      value={settings?.quiet_hours_start ?? 22}
                      disabled={savingSettings}
                      onChange={(e) =>
                        void saveSettings({
                          quiet_hours_start: Number(e.target.value),
                          quiet_hours_end: settings?.quiet_hours_end ?? 6
                        })
                      }
                    >
                      {HOURS.map((h) => (
                        <option key={h} value={h}>
                          {hourLabel(h)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span className="label">Until</span>
                    <select
                      className="select"
                      value={settings?.quiet_hours_end ?? 6}
                      disabled={savingSettings}
                      onChange={(e) =>
                        void saveSettings({
                          quiet_hours_start: settings?.quiet_hours_start ?? 22,
                          quiet_hours_end: Number(e.target.value)
                        })
                      }
                    >
                      {HOURS.map((h) => (
                        <option key={h} value={h}>
                          {hourLabel(h)}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}
            </div>
            <div className="help">
              Bundled email and reminders wait until quiet hours end. Urgent items (escalations, system alerts) still
              send. Nothing is lost — everything is always in your inbox.
            </div>
          </div>

          {flooredRoles.length > 0 && (
            <div className="help">
              {flooredRoles.map(({ role, mode }) => (
                <div key={role}>
                  Your organization requires at least {FLOOR_PHRASE[mode] ?? mode} email for items where you are{" "}
                  {ROLE_LABEL[role] ?? role}. Choices below that are raised automatically for those items.
                </div>
              ))}
            </div>
          )}

          {orderedCategories(Object.keys(grouped)).map((category) => (
            <div key={category} className="card">
              <div style={{ fontWeight: 700, marginBottom: 8 }}>{CATEGORY_LABEL[category] ?? category}</div>
              <div className="tableWrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th style={{ textAlign: "center", width: 160 }}>Email</th>
                      <th style={{ textAlign: "center", width: 120 }}>In-app</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(grouped[category] ?? []).map((served) => {
                      const { type, category: cat, allowed_modes: allowed, default_mode_label: defaultLabel } = served;
                      const meta = notificationTypeDisplay(type, cat);
                      const systemRow = isSystemCategory(cat);
                      const pref = state[type] ?? { email_mode: "default" as UserEmailMode, in_system_enabled: true };
                      const saving = savingType === type;
                      return (
                        <tr key={type}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{meta.label}</div>
                            <div className="help">
                              {meta.description}
                              {defaultLabel && allowed.includes("default") ? ` Default: ${defaultLabel}.` : ""}
                            </div>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {systemRow ? (
                              // The bypass is unchanged: platform alerts always send.
                              <select
                                className="select"
                                value="immediate"
                                disabled
                                aria-label={`Email notifications for ${meta.label}`}
                                title="System notifications cannot be disabled."
                              >
                                <option value="immediate">Immediately</option>
                              </select>
                            ) : allowed.length === 0 ? (
                              // No email exists to configure (refires are in-app only).
                              <span className="muted">—</span>
                            ) : (
                              <select
                                className="select"
                                value={pref.email_mode}
                                disabled={saving}
                                onChange={(e) => void savePreference(type, { email_mode: e.target.value as UserEmailMode })}
                                aria-label={`Email notifications for ${meta.label}`}
                              >
                                {allowed.map((mode) => (
                                  <option key={mode} value={mode}>
                                    {EMAIL_MODE_LABELS[mode]}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={pref.in_system_enabled}
                              disabled={systemRow || saving}
                              onChange={() => void savePreference(type, { in_system_enabled: !pref.in_system_enabled })}
                              aria-label={`In-app notifications for ${meta.label}`}
                              title={systemRow ? "System notifications cannot be disabled." : ""}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </PageLayout>
  );
}
